import React, {FC, useRef, useState} from 'react';
import styled from 'styled-components';

import {addQueryToPath} from '../../../../../libs/shared/core/src/helpers/browser/urlHelpers';
import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {useInitialEffect} from '../../../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {AsyncStatusesEnum} from '../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {reportToBugsnag} from '../../../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {postApiAsync} from '../../../../../libs/shared/core/src/services/http/http';
import {HttpError} from '../../../../../libs/shared/core/src/services/http/httpError';
import {InboxesPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {Skeleton} from '../../../../../libs/shared/ui/src/components/utility/skeleton';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useAppDispatch, useAppSelector} from '../../../../helpers/appReduxHelpers';
import {ApiLenses, LensModel} from '../../../../models/api/lensModel';
import {VisualWorkspaceLens} from '../../../../models/api/visualWorkspaceModel';
import {Dispatch} from '../../../../store/appStore';
import {getCompanyUrl} from '../../../../store/data/company/companySelectors';
import {dataUpdatedAction} from '../../../../store/data/dataGroupsActions';
import {WorkspaceDropReorderLensHandler} from '../../../../store/data/workspace/reorder/types';
import {LensModelMap} from '../../../../store/data/workspace/visualWorkspace/visualWorkspaceHelpers';
import {NavigationLineIcon} from '../../../common/navigation/navigationLineIcon';
import {NavigationLineLabel} from '../../../common/navigation/navigationLineLabel';
import {NavigationLineStandardIcon} from '../../../common/navigation/navigationLineStandardIcon';
import {WorkspacesEditorLensesDropdownContainer} from '../dropdowns/workspacesEditorLensesDropdownContainer';
import {WorkspaceHeader} from '../header/workspaceHeader';
import {CreateLensButton} from '../lensesWorkspace/createLensButton';
import {LensesWorkspaceCreateDialog} from '../lensesWorkspace/dialog/lensesWorkspaceCreateDialog';
import {WorkspaceLineDropTarget} from '../line/workspaceLineDropTarget';
import {WorkspaceLineMore} from '../line/workspaceLineMore';
import {Workspace} from '../workspace';
import {ToggleDropdownLensHandler, workspacesEditorDropdownHeight} from './context/types';
import {LensUpdateHandler, ToggleLensHandler} from './context/workspacesEditorContext';
import {StyledEditModeLabelDiv} from './teammateWorkspaceEditor';
import {WorkspacesEditorLensesContainer} from './workspacesEditorLensesContainer';

/*
 * Props.
 */

interface LensesWorkspaceEditorProps {
  panelId: InboxesPanelId;
  /** Whether this workspace is collapsed or not. */
  isExpanded: boolean;
  /** Event handler to expand/collapse the workspace. */
  onToggleExpand: () => void;
  /** The list of lenses displayed in the workspace. */
  visualWorkspaceLenses: ReadonlyArray<VisualWorkspaceLens>;
  /** Whether workspace is alphabetized. */
  isAlphabetized: boolean;
  /** Event handler for drag and drop re-ordering */
  onDropReorderLens: WorkspaceDropReorderLensHandler;
  /** Event handler to select/remove lenses in P1. */
  onToggleLens: ToggleLensHandler;
  /** Event handler to select/remove lenses in P1. */
  onCreateLens: LensUpdateHandler;
  /** Handler to select/remove lenses in the dropdown. */
  onToggleDropdownLens: ToggleDropdownLensHandler;
  /** Map that stores all the lenses interacted with in dropdown. */
  lensModelMap: LensModelMap;
  isCreateDialogOpen: boolean;
  hideCreateLensDialog: () => void;
}

/*
 * Styles.
 */

const StyledSkeleton = styled(Skeleton)`
  height: 30px;
  width: 90%;
  margin: 3px 5%;
`;

/*
 * Component.
 */

export const LensesWorkspaceEditor: FC<LensesWorkspaceEditorProps> = (props) => {
  const {
    panelId,
    isExpanded,
    onToggleExpand,
    visualWorkspaceLenses,
    onCreateLens,
    isCreateDialogOpen,
    hideCreateLensDialog,
  } = props;
  const companyUrl = useAppSelector(getCompanyUrl);
  const dispatch = useAppDispatch();

  const [loadingStatus, setLoadingStatus] = useState<AsyncStatusesEnum>(AsyncStatusesEnum.LOADING);

  const [shouldShowLensesDropdown, setShouldShowLensesDropdown] = useState<boolean>(false);
  const [lensesDropdownSelectedIds, setLensesDropdownSelectedIds] = useState<Array<number>>([]);

  const onLensesClick = () => setShouldShowLensesDropdown(true);
  const onLensesDismiss = () => {
    setShouldShowLensesDropdown(false);
    setLensesDropdownSelectedIds([]);
  };
  const addLensesRef = useRef<HTMLDivElement>(null);

  const onToggleDropdownLens = (lens: LensModel) => {
    props.onToggleDropdownLens(lens);
    setLensesDropdownSelectedIds([...lensesDropdownSelectedIds, lens.id]);
  };

  useInitialEffect(() => {
    fetchHiddenLenses(visualWorkspaceLenses, setLoadingStatus, companyUrl, dispatch);
  });

  return (
    <Workspace isExpanded={isExpanded}>
      {/* Header. */}
      <WorkspaceHeader
        panelId={panelId}
        interactionId="lenses"
        isExpanded={isExpanded}
        onToggleExpandClick={onToggleExpand}
        isEditingWorkspace
      >
        <Trans id="workspace-lenses-title">Views</Trans>
      </WorkspaceHeader>

      {/* Workspace content. */}
      {isExpanded &&
        renderLensesList(loadingStatus, props, onLensesClick, addLensesRef, shouldShowLensesDropdown)}

      {/* Create view Dialog. */}
      {isCreateDialogOpen && (
        <LensesWorkspaceCreateDialog
          panelId={panelId}
          onRequestClose={hideCreateLensDialog}
          workspaceUpdateHandler={onCreateLens}
        />
      )}

      {/* Dropdown. */}
      {shouldShowLensesDropdown && (
        <WorkspacesEditorLensesDropdownContainer
          anchor={addLensesRef}
          panelId={panelId}
          placement="right-start"
          onRequestClose={onLensesDismiss}
          displayedLensesIds={visualWorkspaceLenses.map((lens) => lens.id)}
          onToggleDropdownLens={onToggleDropdownLens}
          lensesDropdownSelectedIds={lensesDropdownSelectedIds}
          height={workspacesEditorDropdownHeight}
        />
      )}
    </Workspace>
  );
};

/*
 * Helpers.
 */

const fetchHiddenLenses = async (
  visualWorkspaceLenses: ReadonlyArray<VisualWorkspaceLens>,
  setLoadingStatus: (arg: AsyncStatusesEnum) => void,
  companyUrl: string,
  dispatch: Dispatch,
) => {
  const hiddenLensIds = visualWorkspaceLenses.filter(({isSelected}) => !isSelected).map(({id}) => id);

  if (hiddenLensIds.length < 1) {
    setLoadingStatus(AsyncStatusesEnum.LOADED);
    return;
  }

  const url = addQueryToPath(`${companyUrl}/lenses/fetch_batch`);
  const requestBody = {
    ids: hiddenLensIds,
  };

  try {
    const lenses = await postApiAsync(ApiLenses, url, requestBody);
    dispatch(dataUpdatedAction({lenses}));
    setLoadingStatus(AsyncStatusesEnum.LOADED);
  } catch (error) {
    if (error instanceof HttpError) {
      return;
    }

    reportToBugsnag(error); // Report non-HTTP errors to Bugsnag.
    setLoadingStatus(AsyncStatusesEnum.FAILED);
  }
};

function renderLensesList(
  loadingStatus: AsyncStatusesEnum,
  props: LensesWorkspaceEditorProps,
  onLensesClick: () => void,
  addLensesRef: React.RefObject<HTMLDivElement>,
  shouldShowLensesDropdown: boolean,
) {
  const {panelId, isAlphabetized, visualWorkspaceLenses, onDropReorderLens, onToggleLens} = props;

  if (loadingStatus !== AsyncStatusesEnum.LOADED) {
    return (
      <>
        <StyledSkeleton />
        <StyledSkeleton />
      </>
    );
  }

  return (
    <>
      <WorkspacesEditorLensesContainer
        panelId={panelId}
        isAlphabetized={isAlphabetized}
        visualWorkspaceLenses={visualWorkspaceLenses}
        onDropReorderLens={onDropReorderLens}
        onToggleLens={onToggleLens}
        lensModelMap={props.lensModelMap}
      />
      <CreateLensButton panelId={panelId} />
      {renderAddLensesButton(onLensesClick, addLensesRef, shouldShowLensesDropdown)}
    </>
  );
}

function renderAddLensesButton(
  onAddLensesClick: () => void,
  addLensesRef: React.RefObject<HTMLDivElement>,
  shouldShowLensesDropdown: boolean,
) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <div ref={addLensesRef}>
          <WorkspaceLineDropTarget
            indentLevel={0}
            onClick={onAddLensesClick}
            shouldAlphabetizeWorkspace={false}
            workspaceDragItem={undefined}
            isSelected={shouldShowLensesDropdown}
          >
            <NavigationLineIcon>
              <NavigationLineStandardIcon iconName="customViewsSmall" color={theme.palette.blue.shade40} />
            </NavigationLineIcon>
            <NavigationLineLabel>
              <StyledEditModeLabelDiv>
                <Trans id="edit-mode-add-shared-views">Add shared views</Trans>
              </StyledEditModeLabelDiv>
            </NavigationLineLabel>
            <WorkspaceLineMore isEditingWorkspaceButton />
          </WorkspaceLineDropTarget>
        </div>
      )}
    </UserThemeConsumer>
  );
}
